import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./PageLoader.css";

let nextLoaderId = 0;
const activeLoaders = new Map();
const loaderSubscribers = new Set();

const getVisibleLoaderId = () => {
  const loaders = Array.from(activeLoaders.values());
  if (!loaders.length) return null;

  loaders.sort((a, b) => Number(b.force) - Number(a.force) || b.id - a.id);
  return loaders[0].id;
};

const notifyLoaderSubscribers = () => {
  const visibleId = getVisibleLoaderId();
  loaderSubscribers.forEach((subscriber) => subscriber(visibleId));
};

function PageLoader({ force = false, copy = "Loading your workspace" }) {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [visibleLoaderId, setVisibleLoaderId] = useState(null);
  const loaderIdRef = useRef(null);

  if (loaderIdRef.current === null) {
    nextLoaderId += 1;
    loaderIdRef.current = nextLoaderId;
  }

  const loaderId = loaderIdRef.current;
  const isLoginRoute =
    location.pathname === "/login" ||
    location.pathname === "/auth/google/callback";

  useEffect(() => {
    if (isLoginRoute) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 900);
    return () => clearTimeout(timer);
  }, [isLoginRoute, location.pathname]);

  useEffect(() => {
    loaderSubscribers.add(setVisibleLoaderId);
    setVisibleLoaderId(getVisibleLoaderId());

    return () => loaderSubscribers.delete(setVisibleLoaderId);
  }, []);

  useEffect(() => {
    const isActive = !isLoginRoute && (force || loading);

    if (isActive) {
      activeLoaders.set(loaderId, { id: loaderId, force });
    } else {
      activeLoaders.delete(loaderId);
    }

    notifyLoaderSubscribers();

    return () => {
      activeLoaders.delete(loaderId);
      notifyLoaderSubscribers();
    };
  }, [force, isLoginRoute, loaderId, loading]);

  if (isLoginRoute || (!force && !loading) || visibleLoaderId !== loaderId) return null;

  return (
    <div className="page-loader-overlay" role="status" aria-live="polite" aria-label="Loading page">
      <div
        className="wheel-and-hamster"
        role="img"
        aria-label="Orange and tan hamster running in a metal wheel"
      >
        <div className="wheel"></div>
        <div className="hamster">
          <div className="hamster__body">
            <div className="hamster__head">
              <div className="hamster__ear"></div>
              <div className="hamster__eye"></div>
              <div className="hamster__nose"></div>
            </div>
            <div className="hamster__limb hamster__limb--fr"></div>
            <div className="hamster__limb hamster__limb--fl"></div>
            <div className="hamster__limb hamster__limb--br"></div>
            <div className="hamster__limb hamster__limb--bl"></div>
            <div className="hamster__tail"></div>
          </div>
        </div>
        <div className="spoke"></div>
      </div>
      <span className="page-loader-copy">{copy}</span>
    </div>
  );
}

export default PageLoader;
